import { groupBy, maxBy } from 'lodash'
import { makeAutoObservable, toJS } from 'mobx'

import { RootStore } from './RootStore'
import { getSalesforceAuditDataAPI } from '../api/salesforceAudit/salesforceAudit.service'

const initialAuditData: Record<string, any> = {}
export class SalesforceAuditStore {
  apexTriggers: any[] = []
  apexClasses: any[] = []
  rolesData: any[] = []
  profilesData: any[] = []
  permissionSets: any[] = []
  auditData: Record<string, any>
  userPaginatedData: any
  userWithLicense: any
  loading: boolean
  static pageSize = 50
  static userPageSize = 100
  constructor(private rootStore: RootStore) {
    this.auditData = initialAuditData
    this.userWithLicense = initialAuditData
    this.loading = false
    makeAutoObservable(this)
  }
  setLoading(loading: boolean) {
    this.rootStore.salesforceAuditStore.loading = loading
  }
  getLoading() {
    return this.rootStore.salesforceAuditStore.loading
  }
  setAuditData(data: any) {
    this.rootStore.salesforceAuditStore.auditData = data
  }

  setUserData(userWithLicense: any) {
    this.rootStore.salesforceAuditStore.userWithLicense = userWithLicense
  }
  getUserData() {
    const data = this.rootStore.salesforceAuditStore.userWithLicense
    return Array.isArray(data) ? data : data?.records || []
  }

  setUserLicensePaginatedData(userPaginatedData: any) {
    this.rootStore.salesforceAuditStore.userPaginatedData = userPaginatedData
  }
  getUserLicensePaginatedData() {
    const data = this.rootStore.salesforceAuditStore.userPaginatedData
    return Array.isArray(data) ? data : data?.records || []
  }

  getAuditData() {
    return this.rootStore.salesforceAuditStore.auditData
  }

  getLimitsData() {
    return this.getAuditData().limits
  }

  setApexTriggersData(data: any[]) {
    this.apexTriggers = data
  }

  setApexClassesData(data: any[]) {
    this.apexClasses = data
  }

  setRolesData(data: any[]) {
    this.rolesData = data
  }

  setProfilesData(data: any[]) {
    this.profilesData = data
  }

  setPermissionSets(data: any[]) {
    this.permissionSets = data
  }

  getApexTriggersFromStore() {
    return this.apexTriggers
  }

  getApexClassesFromStore() {
    return this.apexClasses
  }

  getRolesDataFomStore() {
    return this.rolesData
  }

  getProfilesDataFromStore() {
    return this.profilesData
  }

  getPermissionSetsFromStore() {
    return this.permissionSets
  }

  getMetadataOverviewData() {
    const data = this.getAuditData()
    const metadataOverviewData = [
      {
        name: 'Apex Classes',
        total: data.apexClasses.length
      },
      {
        name: 'Apex Triggers',
        total: data.apexTriggers.length
      },
      {
        name: 'Roles',
        total: data.roles.length
      },
      {
        name: 'Profiles',
        total: data.profiles.length
      },
      {
        name: 'Permission Sets',
        total: data.permissionSets.length
      }
      //   {
      //     name: 'Custom Permissions',
      //     total: data.customPermissions.length
      //   },
      //   {
      //     name: 'Email Alerts',
      //     total: data.emailAlerts.length
      //   }
    ]
    return metadataOverviewData
  }

  getUsedFileStorage() {
    const files = this.getAuditData().files.map((item: any) => item.record)
    let totalSizeUsed = 0
    if (files.length > 0) {
      totalSizeUsed = files.reduce(
        (total: any, currentFile: any) => total + currentFile['ContentSize'],
        0
      )
    }
    //totalSizeUsed will be in bytes. Convert into mB (1 mB = 1048576 bytes)
    return totalSizeUsed / 1048576
  }

  getApexClassesData() {
    return this.getApexClassesFromStore().map((item: any) => item.record)
  }

  getApexClassById(id: string) {
    return this.getApexClassesData().find((item: any) => item.Id === id)
  }

  getApexTriggersData() {
    return this.getApexTriggersFromStore().map((item: any) => item.record)
  }

  getApexTriggerById(id: string) {
    return this.getApexTriggersData().find((item: any) => item.Id === id)
  }

  getApexTriggerEventString(id: string) {
    const data = this.getApexTriggerById(id)
    if (!data || typeof data !== 'object') return ''

    let eventString = ''
    Object.keys(data).forEach((key) => {
      if (key.startsWith('Usage') && data[key]) {
        if (eventString.length > 0) eventString += ', '
        const eventWithoutPrefix = key.replace(/^Usage/, '')
        const event = eventWithoutPrefix
          .split(/(?=[A-Z])/)
          .map((word) => word.toLowerCase())
        eventString += event.join(' ')
      }
    })
    return eventString
  }

  getRolesData() {
    return this.getRolesDataFomStore().map((item: any) => item.record)
  }

  getRoleById(id: string) {
    return this.getRolesData().find((item: any) => item.Id === id)
  }

  getProfilesData() {
    return this.getProfilesDataFromStore().map((item: any) => item.record)
  }

  getProfileById(id: string) {
    return this.getProfilesData().find((item: any) => item.Id === id)
  }

  getProfilePermissionsData(id: string) {
    const profile = this.getProfileById(id)
    const result: any[] = []

    for (const key in profile) {
      if (key.startsWith('Permissions')) {
        const newKey = key.replace(/^Permissions/, '')
        result.push({ name: newKey, access: profile[key] ? 'Yes' : 'No' })
      }
    }

    return result
  }

  getPermissionSetsData() {
    return this.getPermissionSetsFromStore().map((item: any) => item.record)
  }
  getPermissionSetById(id: string) {
    return this.getPermissionSetsData().find((item: any) => item.Id === id)
  }

  getPermissionSetPermissionsData(id: string) {
    const profile = this.getPermissionSetById(id)
    const result: any[] = []

    for (const key in profile) {
      if (key.startsWith('Permissions')) {
        const newKey = key.replace(/^Permissions/, '')
        result.push({ name: newKey, access: profile[key] ? 'Yes' : 'No' })
      }
    }

    return result
  }
  getLicenses() {
    return this.getAuditData().userLicenses.map((item: any) => item.record)
  }
  getPermissionSetsLicenses() {
    return this.getAuditData().permissionSetLicenses.map(
      (item: any) => item.record
    )
  }
  getPermissionSetsLicensesById(id: string) {
    return this.getPermissionSetsLicenses().find((item: any) => item.Id === id)
  }

  getLicenseById(id: string) {
    return this.getLicenses().find((item: any) => item.Id === id)
  }
  getTotalLicenses() {
    const licenses = this.getLicenses()
    let count = 0
    for (const license of licenses) {
      count += license.TotalLicenses
    }
    return count
  }

  getTotalPermissionSetsLicenses() {
    const licenses = this.getPermissionSetsLicenses()
    let count = 0
    for (const license of licenses) {
      count += license.TotalLicenses
    }
    return count
  }

  getUsedLicenses() {
    const licenses = this.getLicenses()
    let count = 0
    for (const license of licenses) {
      count += license.UsedLicenses
    }
    return count
  }

  getUsedPermissionSetsLicenses() {
    const licenses = this.getPermissionSetsLicenses()
    let count = 0
    for (const license of licenses) {
      count += license.UsedLicenses
    }
    return count
  }

  getMostCommonLicense(): any {
    const licenses = this.getLicenses()
    const maxUsedLicense = maxBy(licenses, 'UsedLicenses')
    return maxUsedLicense
  }

  getMostCommonPermissionSetsLicenses(): any {
    const licenses = this.getPermissionSetsLicenses()
    const maxUsedLicense = maxBy(licenses, 'UsedLicenses')
    return maxUsedLicense
  }

  getPermissionSetsLicensesDistribution(): any {
    const distribution = []
    const licenses = this.getPermissionSetsLicenses()
    for (const license of licenses) {
      distribution.push({
        name: license.MasterLabel,
        usage:
          (license.UsedLicenses / this.getUsedPermissionSetsLicenses()) * 100
      })
    }
    return distribution
  }

  getTotalLicenseDistribution(): any {
    const distribution = []
    const standardLicenses = this.getLicenses()
    const permissionLicenses = this.getPermissionSetsLicenses()

    const totalUsedLicenses =
      this.getUsedLicenses() + this.getUsedPermissionSetsLicenses()

    for (const license of standardLicenses) {
      distribution.push({
        name: license.Name,
        usage: (license.UsedLicenses / totalUsedLicenses) * 100
      })
    }

    for (const license of permissionLicenses) {
      distribution.push({
        name: license.MasterLabel,
        usage: (license.UsedLicenses / totalUsedLicenses) * 100
      })
    }

    return distribution
  }

  getLicenseDistribution(): any {
    const distribution = []
    const licenses = this.getLicenses()
    for (const license of licenses) {
      distribution.push({
        name: license.Name,
        usage: (license.UsedLicenses / this.getUsedLicenses()) * 100
      })
    }
    return distribution
  }

  async getSalesforceAuditFromAPI() {
    this.setLoading(true)
    try {
      const { data } = await getSalesforceAuditDataAPI()
      this.setAuditData(data)
    } finally {
      this.setLoading(false)
    }
  }

  async reset() {
    this.loading = false
    this.auditData = initialAuditData
    this.userWithLicense = initialAuditData
  }
}
